import React, { useState } from "react";
import { BrandingDisabledIf, DisabledPrompt, Link, BCMSCurrency, ModalOpen, useBCMSModal, useModel, BrandingStyle } from "bcms-react";
import "./styles.css";
import { WidgetContainer, WidgetView } from 'components/shared';
import { PrimaryButton } from "components/ui";
import CustomAccordion, { CustomAccordionContent, CustomAccordionToggle } from "components/custom-ui/CustomAccordion";
import { ImageAsset } from "res";

import { Bill } from "model";
import { CheckmarkBanner } from "components/shared/views/ConfirmBannerView";
import Util from "classes/Util";

/**
 * @function PaymentsWidget
 * @return {React.Component}
 * @exported true
 * @description The Payments widget
 */
export default function PaymentsWidget(props) {
  const { open } = useBCMSModal();
  const [ successState, setSuccessState ] = useState({ "open" : false, "text" : "" });

  const state = { successState, setSuccessState };
  const onClick = evt => open("MakeAPaymentModal", { success: state });

  return(
    <WidgetContainer className="payment-widget">
      <WidgetView text="Payments" dataPoint="Payments_Header">
        <BrandingDisabledIf id="rxp17_courseID_abi22" exp={ value => value !== null && value !== undefined }>
          <Link to="/rxpmw" className="show-on-mobile" dataPoint="Payments_Payment_Center">Payment Center</Link> 
          <Link to="/payments" className="show-on-desktop" dataPoint="Payments_Payment_Center">Payment Center</Link>
        </BrandingDisabledIf>

        { successState.open && <CheckmarkBanner text={ successState.text } onClick={ () => setSuccessState({ ...successState, "open" : false }) } /> }
        <PrimaryButton text="Make A Payment" onClick={ onClick } data-point="Payments_Make_Payment" />

        <BillDueAccordion amountDue="$1,100.33" ui={ props.ui } state={ state } />
        <PendingPaymentAccordion amountPaid="$124.99" ui={ props.ui } state={ state } />        
      </WidgetView>
    </WidgetContainer>
  );
}

/**
 * @function BillDueAccordion
 * @return {React.Component}
 * @exported true
 * @description The Bill Due accordion
 * @example
 * <BillDueAccordion>
 *  <PendingPaymentAccordion />
 * </BillDueAccordion>
 */
export function BillDueAccordion(props) {
  // Get all bills that have a payment due and create the accordion items and update the total amount
  const { bills, amount } = Bill.getDueBills(Bill.initAll()).reduce((result, bill) => {
    const component = <BillAccordionItem key={ "bill_" + bill.id } state={ props.state } id={ bill.id } isDue={ true } billerName={ bill.name } amount={ bill.amount.format() } date={ bill.due.format("m/d/Y") } logo={ bill.logo } />
    return { "bills" : [ ...result.bills, component ], "amount" : result.amount + parseFloat(bill.amount.amount) };
  }, { "bills" : [], "amount" : 0 });

  const isActive = props.ui ? (props.ui.paymentsWidgeteBills.isActive ?? false) : false;

  return(
    <CustomAccordion isOpen={ isActive } className="accordion-container" data-point="Payments_Upcoming_Bills" activeClass="open-accordion">
      <CustomAccordionToggle>
        <div className="flex-container flex-horizontal flex-width align-center">
          <BrandingStyle style={{"color": "payment_widget_section_text_abi22"}} 
            className="set-width highlighted-text payment-quantity text-center left-column" 
            element={
              <p>{ bills.length }</p>
            } />

          <div className="flex-container flex-vertical flex-width space-between accordion-toggle-details">
          <BrandingStyle style={{"color": "payment_widget_section_text_abi22"}} 
            className="highlighted-text"
            element={
              <b>eBills Due in 30 Days</b>
            } />
         
            <p>Amount Due: <b>{ BCMSCurrency.toCurrency(amount) }</b></p>
          </div>
        </div>
      </CustomAccordionToggle>
      <CustomAccordionContent>
        { bills }
      </CustomAccordionContent>
    </CustomAccordion>
  );
}

/**
 * @function PendingPaymentAccordion
 * @return {React.Component}
 * @exported true
 * @description The Pending Payments accordion
 */
export function PendingPaymentAccordion(props) {
  // Get all bills that have a pending payment and create the accordion items and update the total amount
  const { bills, amount } = Bill.getBillsPendingPayment(Bill.initAll()).reduce((result, bill) => {
    const component = <BillAccordionItem key={ "bill_" + bill.id } state={ props.state } id={ bill.id } billerName={ bill.name } amount={ bill.transaction.amount.format() } date={ bill.due.format("m/d/Y") } logo={ bill.logo } />
    return { "bills" : [ ...result.bills, component ], "amount" : result.amount + parseFloat(bill.transaction.amount.amount) };
  }, { "bills" : [], "amount" : 0 });

  const isActive = props.ui ? (props.ui.paymentsWidgetPending.isActive ?? false) : false;

  return(
    <CustomAccordion isOpen={ isActive } className="accordion-container" activeClass="open-accordion" data-point="Payments_Pending_Bills">
      <CustomAccordionToggle>
        <div className="flex-container flex-horizontal flex-width align-center">
        <BrandingStyle style={{"color": "payment_widget_section_text_abi22"}} 
            className="set-width highlighted-text payment-quantity text-center left-column"
            element={
              <p>{ bills.length }</p>
            } />
          <div className="flex-container flex-vertical flex-width space-between accordion-toggle-details">
          <BrandingStyle style={{"color": "payment_widget_section_text_abi22"}} 
            className="highlighted-text"
            element={
              <b>Pending</b>
            } />
            <p>Amount Paid: <b>{ BCMSCurrency.toCurrency(amount) }</b></p>
          </div>
        </div>
      </CustomAccordionToggle>
      <CustomAccordionContent>
        { bills }
      </CustomAccordionContent>
    </CustomAccordion>
  );
}

/**
 * @function BillAccordionItem
 * @param {OBJECT} props 
 * @param {STRING} amount - The amount that is due or was paid.
 * @param {STRING} date - Date of when bill is due on or when bill was paid.
 * @param {STRING} name - Name of the biller.
 * @param {ASSET} logo - Logo of the biller.
 * @param {BOOLEAN} isDue - if isDue is true, "due on" text is shown, if false or not set, "to be paid on" text is shown.
 * @return {React.Component}
 * @exported true
 * @description This is each biller row in the expanded accordion.
 * @example
 * <BillAccordionItem amount="87.65" date="10/14/20XX" name="Biller Name" logo="billerLogo" isDue={true} >
 */
export function BillAccordionItem(props) {
  const { open } = useBCMSModal();
  const onClick = evt => open("MakeAPaymentModal", { success: props.state ?? {}, id: props.id ?? "" });
  
  const amount = props.amount ?? "";
  const date = props.date ?? "";
  const name = props.name ?? "";
  const logo = props.logo ?? "";
  const billerName = props.billerName ?? "";
  
  const isDue = props.isDue !== true ? (<p className="flex-container flex-horizontal flex-width">{amount} to be paid on {date}</p>) : 
    (<div className="flex-container flex-horizontal flex-width">
      <p className="flex-width">{amount} due on {date}</p>
      <Link className="accordion-pay-button" onClick={ onClick }>Pay</Link>
    </div>);

  return(
    <div className="flex-container flex-horizontal flex-width accordion-row">
      <div className="set-width left-column text-center">
        <ImageAsset id={logo} className="payments-biller-image" />
      </div>
      <div className="flex-container flex-vertical flex-width biller-details">
        <b>{billerName}</b>
        {isDue}
      </div>
    </div>
  );
}